import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, ArrowLeft, Home } from "lucide-react";

/**
 * Class-based ErrorBoundary: cattura errori di rendering / lifecycle dei figli
 * per evitare che la pagina diventi bianca.
 *
 * Si reinizializza automaticamente al cambio di route (resetKey).
 */
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Logging utile in produzione: console + telemetria opzionale futura
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Reset automatico quando l'utente cambia route
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback({
        error: this.state.error,
        reset: () => this.setState({ hasError: false, error: null }),
      });
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, reset, onBack, onHome }) {
  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="max-w-md w-full p-6 rounded-2xl border border-red-500/20 bg-[#18181b]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Qualcosa è andato storto</h2>
            <p className="text-zinc-400 text-xs mt-0.5">
              Si è verificato un errore in questa pagina. I tuoi dati sono al sicuro.
            </p>
          </div>
        </div>

        {error?.message && (
          <pre className="bg-black/40 text-red-300/80 text-[11px] rounded-lg p-3 mb-4 overflow-x-auto max-h-32 font-mono">
            {String(error.message).slice(0, 400)}
          </pre>
        )}

        <div className="flex flex-wrap gap-2">
          <Button onClick={reset} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="error-boundary-retry">
            <RefreshCw className="w-4 h-4 mr-2" />
            Riprova
          </Button>
          <Button onClick={onBack} variant="outline" className="border-white/10 text-white" data-testid="error-boundary-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <Button onClick={onHome} variant="ghost" className="text-zinc-400" data-testid="error-boundary-home">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>
      </div>
    </div>
  );
}

export function RouteErrorBoundary({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const fallback = ({ error, reset }) => (
    <DefaultFallback
      error={error}
      reset={reset}
      onBack={() => { reset(); navigate(-1); }}
      onHome={() => { reset(); navigate("/", { replace: true }); }}
    />
  );

  return (
    <ErrorBoundaryClass resetKey={location.pathname} fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  );
}
